import { useCallback, useState } from "react";

// Rows the user has added or struck through but not saved yet, keyed by whatever identifies a row
// on that tab. Added rows have to stay on screen before anything is picked for them, and removals
// are staged so an accidental click on the trash costs one click to undo rather than a discard.
export default function useStagedRows() {
    const [added, setAdded] = useState<string[]>([]);
    const [removed, setRemoved] = useState<string[]>([]);

    const isAdded = useCallback((key: string) => added.includes(key), [added]);
    const isRemoved = useCallback((key: string) => removed.includes(key), [removed]);

    // Adding brings a struck-through row back rather than leaving it marked for removal
    const addRows = useCallback((keys: string[]) => {
        setAdded(prev => [...prev, ...keys.filter(k => !prev.includes(k))]);
        setRemoved(prev => prev.filter(k => !keys.includes(k)));
    }, []);

    const removeRow = useCallback((key: string) => {
        setRemoved(prev => prev.includes(key) ? prev : [...prev, key]);
    }, []);

    const restoreRow = useCallback((key: string) => {
        setRemoved(prev => prev.filter(k => k !== key));
    }, []);

    const clear = useCallback(() => {
        setAdded([]);
        setRemoved([]);
    }, []);

    return { added, removed, isAdded, isRemoved, addRows, removeRow, restoreRow, clear };
}
